import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import { useField } from '@unform/core';
import { Container, Error } from './styles';
import FormCheck from 'react-bootstrap/FormCheckInput';
import moment from 'moment';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css";
import PasswordStrengthBar from 'react-password-strength-bar';
import Select from 'react-select';
import { TreeSelect } from 'antd';
import ColorPicker from '../ColorPicker';
import { formSelectStyles, formSelectControlFullWidth } from './formSelectStyles';
import Api from '../../services/api';
import { fetchListTreeFlatByListTreeID } from '../../services/functions';

const MAX_LENGTH = 150;
/** Limite de caracteres nos rótulos das opções do react-select em listas muito grandes. */
const SELECT_OPTION_LABEL_CAP = 4000;

const SINGLE_SELECT_TYPES = [
    'select', 'selectculture', 'selectfuel', 'selectcountry', 'selectCuringBarn',
    'selectyn', 'selectynu', 'selectasnn', 'selectasn', 'selecthousing',
    'selectcharge', 'selectli', 'selecttimezone', 'selectcustom'
];

const FIXED_OPTIONS = {
    selectyn: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }],
    selectynu: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'Unsure', label: 'Unsure' }],
    selectasnn: [{ value: 'Always', label: 'Always' }, { value: 'Sometimes', label: 'Sometimes' }, { value: 'Never', label: 'Never' }, { value: 'NA', label: 'NA' }],
    selectasn: [{ value: 'All', label: 'All' }, { value: 'Some', label: 'Some' }, { value: 'None', label: 'None' }],
    selecthousing: [
        { value: 'All', label: 'All' }, { value: 'More than half', label: 'More than half' },
        { value: 'Approximaterly half', label: 'Approximaterly half' }, { value: 'Less than half', label: 'Less than half' }, { value: 'None', label: 'None' }
    ],
    selectcharge: [{ value: 'Full Cost', label: 'Full Cost' }, { value: 'Subsidised', label: 'Subsidised' }, { value: 'Free of Charge', label: 'Free of Charge' }],
    selectli: [{ value: 'Local', label: 'Local' }, { value: 'Imported', label: 'Imported' }, { value: 'Free of Charge', label: 'Free of Charge' }],
};

const TIMEZONE_OPTIONS = [
    { value: 'Morocco Standard Time', label: '(GMT) Casablanca' },
    { value: 'GMT Standard Time', label: '(GMT) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London' },
    { value: 'Greenwich Standard Time', label: '(GMT) Monrovia, Reykjavik' },
    { value: 'W. Europe Standard Time', label: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna' },
    { value: 'Central Europe Standard Time', label: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague' },
    { value: 'Romance Standard Time', label: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris' },
    { value: 'Central European Standard Time', label: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb' },
    { value: 'W. Central Africa Standard Time', label: '(GMT+01:00) West Central Africa' },
    { value: 'Jordan Standard Time', label: '(GMT+02:00) Amman' },
    { value: 'GTB Standard Time', label: '(GMT+02:00) Athens, Bucharest, Istanbul' },
    { value: 'Middle East Standard Time', label: '(GMT+02:00) Beirut' },
    { value: 'Egypt Standard Time', label: '(GMT+02:00) Cairo' },
    { value: 'South Africa Standard Time', label: '(GMT+02:00) Harare, Pretoria' },
    { value: 'FLE Standard Time', label: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius' },
    { value: 'Israel Standard Time', label: '(GMT+02:00) Jerusalem' },
    { value: 'E. Europe Standard Time', label: '(GMT+02:00) Minsk' },
    { value: 'Namibia Standard Time', label: '(GMT+02:00) Windhoek' },
    { value: 'Arabic Standard Time', label: '(GMT+03:00) Baghdad' },
    { value: 'Arab Standard Time', label: '(GMT+03:00) Kuwait, Riyadh' },
    { value: 'Russian Standard Time', label: '(GMT+03:00) Moscow, St. Petersburg, Volgograd' },
    { value: 'E. Africa Standard Time', label: '(GMT+03:00) Nairobi' },
    { value: 'Georgian Standard Time', label: '(GMT+03:00) Tbilisi' },
    { value: 'Iran Standard Time', label: '(GMT+03:30) Tehran' },
    { value: 'Arabian Standard Time', label: '(GMT+04:00) Abu Dhabi, Muscat' },
    { value: 'Azerbaijan Standard Time', label: '(GMT+04:00) Baku' },
    { value: 'Mauritius Standard Time', label: '(GMT+04:00) Port Louis' },
    { value: 'Caucasus Standard Time', label: '(GMT+04:00) Yerevan' },
    { value: 'Afghanistan Standard Time', label: '(GMT+04:30) Kabul' },
    { value: 'Ekaterinburg Standard Time', label: '(GMT+05:00) Ekaterinburg' },
    { value: 'Pakistan Standard Time', label: '(GMT+05:00) Islamabad, Karachi' },
    { value: 'West Asia Standard Time', label: '(GMT+05:00) Tashkent' },
    { value: 'India Standard Time', label: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
    { value: 'Sri Lanka Standard Time', label: '(GMT+05:30) Sri Jayawardenepura' },
    { value: 'Nepal Standard Time', label: '(GMT+05:45) Kathmandu' },
    { value: 'N. Central Asia Standard Time', label: '(GMT+06:00) Almaty, Novosibirsk' },
    { value: 'Central Asia Standard Time', label: '(GMT+06:00) Astana, Dhaka' },
    { value: 'Myanmar Standard Time', label: '(GMT+06:30) Yangon (Rangoon)' },
    { value: 'SE Asia Standard Time', label: '(GMT+07:00) Bangkok, Hanoi, Jakarta' },
    { value: 'North Asia Standard Time', label: '(GMT+07:00) Krasnoyarsk' },
    { value: 'China Standard Time', label: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi' },
    { value: 'North Asia East Standard Time', label: '(GMT+08:00) Irkutsk, Ulaan Bataar' },
    { value: 'Singapore Standard Time', label: '(GMT+08:00) Kuala Lumpur, Singapore' },
    { value: 'W. Australia Standard Time', label: '(GMT+08:00) Perth' },
    { value: 'Taipei Standard Time', label: '(GMT+08:00) Taipei' },
    { value: 'Tokyo Standard Time', label: '(GMT+09:00) Osaka, Sapporo, Tokyo' },
    { value: 'Korea Standard Time', label: '(GMT+09:00) Seoul' },
    { value: 'Yakutsk Standard Time', label: '(GMT+09:00) Yakutsk' },
    { value: 'Cen. Australia Standard Time', label: '(GMT+09:30) Adelaide' },
    { value: 'AUS Central Standard Time', label: '(GMT+09:30) Darwin' },
    { value: 'E. Australia Standard Time', label: '(GMT+10:00) Brisbane' },
    { value: 'AUS Eastern Standard Time', label: '(GMT+10:00) Canberra, Melbourne, Sydney' },
    { value: 'West Pacific Standard Time', label: '(GMT+10:00) Guam, Port Moresby' },
    { value: 'Tasmania Standard Time', label: '(GMT+10:00) Hobart' },
    { value: 'Vladivostok Standard Time', label: '(GMT+10:00) Vladivostok' },
    { value: 'Central Pacific Standard Time', label: '(GMT+11:00) Magadan, Solomon Is., New Caledonia' },
    { value: 'New Zealand Standard Time', label: '(GMT+12:00) Auckland, Wellington' },
    { value: 'Fiji Standard Time', label: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.' },
    { value: "Tonga Standard Time", label: "(GMT+13:00) Nuku'alofa" },
    { value: 'Azores Standard Time', label: '(GMT-01:00) Azores' },
    { value: 'Cape Verde Standard Time', label: '(GMT-01:00) Cape Verde Is.' },
    { value: 'Mid-Atlantic Standard Time', label: '(GMT-02:00) Mid-Atlantic' },
    { value: 'E. South America Standard Time', label: '(GMT-03:00) Brasilia' },
    { value: 'Argentina Standard Time', label: '(GMT-03:00) Buenos Aires' },
    { value: 'SA Eastern Standard Time', label: '(GMT-03:00) Georgetown' },
    { value: 'Greenland Standard Time', label: '(GMT-03:00) Greenland' },
    { value: 'Montevideo Standard Time', label: '(GMT-03:00) Montevideo' },
    { value: 'Newfoundland Standard Time', label: '(GMT-03:30) Newfoundland' },
    { value: 'Atlantic Standard Time', label: '(GMT-04:00) Atlantic Time (Canada)' },
    { value: 'SA Western Standard Time', label: '(GMT-04:00) La Paz' },
    { value: 'Central Brazilian Standard Time', label: '(GMT-04:00) Manaus' },
    { value: 'Pacific SA Standard Time', label: '(GMT-04:00) Santiago' },
    { value: 'Venezuela Standard Time', label: '(GMT-04:30) Caracas' },
    { value: 'SA Pacific Standard Time', label: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco' },
    { value: 'Eastern Standard Time', label: '(GMT-05:00) Eastern Time (US & Canada)' },
    { value: 'US Eastern Standard Time', label: '(GMT-05:00) Indiana (East)' },
    { value: 'Central America Standard Time', label: '(GMT-06:00) Central America' },
    { value: 'Central Standard Time', label: '(GMT-06:00) Central Time (US & Canada)' },
    { value: 'Central Standard Time (Mexico)', label: '(GMT-06:00) Guadalajara, Mexico City, Monterrey' },
    { value: 'Canada Central Standard Time', label: '(GMT-06:00) Saskatchewan' },
    { value: 'US Mountain Standard Time', label: '(GMT-07:00) Arizona' },
    { value: 'Mountain Standard Time (Mexico)', label: '(GMT-07:00) Chihuahua, La Paz, Mazatlan' },
    { value: 'Mountain Standard Time', label: '(GMT-07:00) Mountain Time (US & Canada)' },
    { value: 'Pacific Standard Time', label: '(GMT-08:00) Pacific Time (US & Canada)' },
    { value: 'Pacific Standard Time (Mexico)', label: '(GMT-08:00) Tijuana, Baja California' },
    { value: 'Alaskan Standard Time', label: '(GMT-09:00) Alaska' },
    { value: 'Hawaiian Standard Time', label: '(GMT-10:00) Hawaii' },
    { value: 'Samoa Standard Time', label: '(GMT-11:00) Midway Island, Samoa' },
    { value: 'Dateline Standard Time', label: '(GMT-12:00) International Date Line West' },
];

/** Atualiza recursivamente a árvore inserindo filhos no nó cujo key === parentKey. */
const updateTreeData = (list, parentKey, children) =>
    list.map(node => {
        if (node.key === parentKey) return { ...node, children };
        if (node.children?.length) return { ...node, children: updateTreeData(node.children, parentKey, children) };
        return node;
    });

/**
 * Determina se um nó da árvore deve ficar desabilitado.
 * Mode 3 (SelectOne-leaf): desabilita qualquer nó que tenha filhos.
 * Modos 1/2 + allowSelectOnlyChildrenNodes: desabilita nós pai.
 */
const shouldDisableTreeNode = (item, optionsModeView, allowSelectOnlyChildrenNodes) => {
    if (optionsModeView === 3) return !!item.hasChildren;
    return !!(allowSelectOnlyChildrenNodes && item.hasChildren);
};

/** Busca um nó na árvore pelo seu value (suporta comparação numérica). */
const findNodeByValueInTree = (nodes, targetValue) => {
    const num = Number(targetValue);
    for (const n of nodes) {
        if (n.value === targetValue || (!Number.isNaN(num) && n.value === num)) return n;
        if (n.children?.length) {
            const found = findNodeByValueInTree(n.children, targetValue);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Dado um conjunto de IDs selecionados e a lista plana completa da árvore,
 * retorna apenas os IDs que são nós folha (sem filhos) descendentes dos IDs selecionados.
 */
const resolveToLeafIds = (selectedIds, flat) => {
    if (!flat?.length) return selectedIds;
    const parentSet = new Set();
    const parentMap = new Map();
    flat.forEach(i => {
        const cid = Number(i.listTreeItemID);
        const pid = i.parentID != null ? Number(i.parentID) : null;
        if (pid != null) {
            parentSet.add(pid);
            parentMap.set(cid, pid);
        }
    });
    const selectedSet = new Set(selectedIds.map(Number));
    const seen = new Set();
    const leafIds = [];
    flat.forEach(i => {
        const id = Number(i.listTreeItemID);
        if (parentSet.has(id)) return;
        let cur = id;
        while (cur != null) {
            if (selectedSet.has(cur)) {
                if (!seen.has(id)) { seen.add(id); leafIds.push(id); }
                break;
            }
            cur = parentMap.has(cur) ? parentMap.get(cur) : null;
        }
    });
    return leafIds.length > 0 ? leafIds : selectedIds;
};

/** Há seleção salva que precisa de rótulos na árvore (evita mostrar só o ID após reload). */
const hasPersistedListTreeValue = (valueProp, optionsModeView) => {
    if (valueProp == null || valueProp === '') return false;
    if (optionsModeView === 2) {
        try {
            const arr = typeof valueProp === 'string' ? JSON.parse(valueProp) : valueProp;
            return Array.isArray(arr) && arr.length > 0;
        } catch {
            return false;
        }
    }
    const n = Number(valueProp);
    return !Number.isNaN(n);
};

/** Monta treeData a partir da lista plana retornada por ListTree/getItems. */
const buildTreeDataFromFlatItems = (flat, optionsModeView, allowSelectOnlyChildrenNodes, maxDepthLimit) => {
    if (!flat?.length) return [];
    const hasChild = new Set();
    flat.forEach(i => {
        if (i.parentID != null) hasChild.add(i.parentID);
    });

    const depthById = new Map();
    const byParent = new Map();
    flat.forEach(i => {
        if (i.parentID != null) {
            const pid = Number(i.parentID);
            if (!byParent.has(pid)) byParent.set(pid, []);
            byParent.get(pid).push(i.listTreeItemID);
        }
    });
    const queue = [];
    flat.filter(i => i.parentID == null).forEach(r => queue.push([r.listTreeItemID, 0]));
    while (queue.length) {
        const [id, d] = queue.shift();
        depthById.set(id, d);
        const pid = Number(id);
        (byParent.get(pid) || []).forEach(cid => queue.push([cid, d + 1]));
    }

    const limitReached = listTreeItemId =>
        maxDepthLimit != null && (depthById.get(listTreeItemId) ?? 0) >= maxDepthLimit;

    const toNode = item => ({
        title: item.value,
        value: item.listTreeItemID,
        key: item.listTreeItemID,
        depth: depthById.get(item.listTreeItemID) ?? 0,
        isLeaf: !hasChild.has(item.listTreeItemID) || limitReached(item.listTreeItemID),
        disabled: shouldDisableTreeNode(
            { hasChildren: hasChild.has(item.listTreeItemID) && !limitReached(item.listTreeItemID) },
            optionsModeView,
            allowSelectOnlyChildrenNodes,
        ),
        children: [],
    });
    const nodeMap = new Map(flat.map(i => [i.listTreeItemID, toNode(i)]));
    const roots = [];
    flat.forEach(i => {
        const n = nodeMap.get(i.listTreeItemID);
        if (i.parentID == null || !nodeMap.has(i.parentID)) roots.push(n);
        else nodeMap.get(i.parentID).children.push(n);
    });
    return roots;
};

/** Compara valor do formulário com valor da opção (suporta boolean vs 1/0 da API). */
const optionValueMatches = (formValue, optionValue) => {
    if (formValue === optionValue) return true;
    const sForm = String(formValue);
    const sOpt = String(optionValue);
    if (sForm === sOpt) return true;
    if (optionValue === true && (formValue === 1 || formValue === '1')) return true;
    if (optionValue === false && (formValue === 0 || formValue === '0')) return true;
    return false;
};

/** Com blockedValueAlert o control do react-select já recebe borda vermelha; não repetir alert-input-hasValue no container (evita borda dupla com o CSS do Report). */
const reactSelectOuterClassName = (classNameProp, blockedValueAlert) => {
    if (!classNameProp) return undefined;
    if (!blockedValueAlert) return classNameProp;
    const parts = String(classNameProp)
        .split(/\s+/)
        .filter(Boolean)
        .filter((c) => c !== 'alert-input-hasValue');
    return parts.length ? parts.join(' ') : undefined;
};

const FormInput = ({ inputType, name, placeholder, options, hideAlert, min, max, isDash = null, customOptions = null, refNumber = null, checked = null, styleGlide = null, isSimulation = false, value: valueProp, blockedValueAlert = false, className: classNameProp, ...rest }) => {
    if (!placeholder && inputType !== "select" && inputType !== undefined && typeof inputType?.startsWith === 'function' && inputType.startsWith("select")) {
        placeholder = { value: "", text: "Select an option" };
    }
    const inputRef = useRef(null);
    const optionsRef = useRef([]);
    const selectValueRef = useRef(null);
    const setSelectValueRef = useRef(() => { });
    const refDisabledRef = useRef(false);
    const setRefDisabledRef = useRef(() => { });
    const selectProxyRef = useRef(null);
    const previousValuePropRef = useRef(undefined);

    const [isFocused, setIsFocused] = useState(false);
    const [isFilled, setIsFilled] = useState(false);
    const { fieldName, defaultValue, error, registerField } = useField(name);
    const [startDate, setStartDate] = useState(new Date());
    const [inputValue, setInputValue] = useState('');
    const [customOptionsValues, setCustomOptionsValues] = useState([]);
    const [multipleValues, setMultipleValues] = useState([]);
    const [selectValue, setSelectValue] = useState(null);
    const [refDisabled, setRefDisabled] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [treeLoading, setTreeLoading] = useState(false);
    const [treeValue, setTreeValue] = useState(undefined);
    const [treeLazyExpand, setTreeLazyExpand] = useState(true);
    const treeValueRef = useRef(undefined);
    const lastListTreeFetchKeyRef = useRef('');

    // react-select aplica `id` no container E em inputId no input — ids duplicados fazem querySelectorAll
    // encontrar 2 nós e setElementDisabled pintar o div externo + blockedValueAlert pintar o control (borda dupla).
    const {
        onChange: onChangeProp,
        listTreeID,
        optionsModeView,
        allowSelectOnlyChildrenNodes,
        maxTreeDepth,
        parentItemIDsFilter,
        isDisabled: _omitIsDisabledFromRest,
        id: omitReactSelectRootId,
        ...restSelect
    } = rest;
    const reactSelectInputId = omitReactSelectRootId ?? fieldName;

    const effectiveMaxTreeDepth = useMemo(() => {
        if (maxTreeDepth == null || maxTreeDepth === '') return null;
        const n = Number(maxTreeDepth);
        if (!Number.isFinite(n) || n < 0) return null;
        return Math.floor(n);
    }, [maxTreeDepth]);

    const filterSignature = useMemo(() => {
        if (!Array.isArray(parentItemIDsFilter)) return '';
        if (parentItemIDsFilter.length === 0) return 'EMPTY';
        return [...parentItemIDsFilter]
            .map(Number)
            .filter(n => !Number.isNaN(n))
            .sort((a, b) => a - b)
            .join(',');
    }, [parentItemIDsFilter]);

    const restWithClassName =
        classNameProp != null && classNameProp !== ''
            ? { ...rest, className: classNameProp }
            : rest;

    const isSingleSelectReactSelect = SINGLE_SELECT_TYPES.includes(inputType) && !(inputType === 'selectcustom' && (refNumber === '7309' || refNumber === '7316'));

    const hasPlaceholder = placeholder != null && placeholder !== '';
    const selectPlaceholderText = hasPlaceholder
        ? ((typeof placeholder === 'object') ? (placeholder.text ?? placeholder.value ?? 'Select an option') : String(placeholder))
        : '';
    const placeholderOptionValue = (hasPlaceholder && typeof placeholder === 'object') ? (placeholder.value ?? '') : '';

    const isHiddenOptionType = inputType === 'selectculture' || inputType === 'selectfuel' || inputType === 'selectcountry' || inputType === 'selectCuringBarn';
    const optionsHiddenSignature = isHiddenOptionType && options?.length ? options.map((o) => (o?.hidden ? '1' : '0')).join(',') : '';

    const singleSelectOptions = useMemo(() => {
        if (!isSingleSelectReactSelect) return [];
        if (FIXED_OPTIONS[inputType]) return FIXED_OPTIONS[inputType];
        if (inputType === 'selecttimezone') return TIMEZONE_OPTIONS;
        if (inputType === 'select') {
            if (!options?.length) return [{ value: 0, label: 'no data' }];
            const labelFrom = (opt) => (opt?.label ?? opt?.text ?? String(opt?.value ?? ''));
            return options.map((opt) => {
                const raw = labelFrom(opt);
                const label = raw?.length > SELECT_OPTION_LABEL_CAP ? `${String(raw).substring(0, SELECT_OPTION_LABEL_CAP)}…` : raw;
                return { value: opt?.value, label };
            });
        }
        if (isHiddenOptionType) {
            if (!options?.length) return [{ value: 0, label: 'no data' }];
            return options
                .filter((opt) => {
                    if (opt == null) return false;
                    if (opt.hidden) {
                        return valueProp != null && valueProp !== '' && String(opt.value) === String(valueProp);
                    }
                    return true;
                })
                .map((opt) => ({ value: opt?.value, label: (opt?.text ?? opt?.label)?.length > SELECT_OPTION_LABEL_CAP ? `${String(opt?.text ?? opt?.label).substring(0, SELECT_OPTION_LABEL_CAP)}…` : (opt?.text ?? opt?.label ?? String(opt?.value ?? '')) }));
        }
        if (inputType === 'selectcustom') {
            return customOptionsValues.filter(Boolean).map((opt) => ({ value: opt, label: (opt?.length > SELECT_OPTION_LABEL_CAP ? `${String(opt).substring(0, SELECT_OPTION_LABEL_CAP)}…` : String(opt ?? '')) }));
        }
        return [];
    }, [inputType, options, customOptionsValues, isSingleSelectReactSelect, valueProp, optionsHiddenSignature]);

    useEffect(() => { optionsRef.current = singleSelectOptions; }, [singleSelectOptions]);
    useEffect(() => {
        selectValueRef.current = selectValue;
        setSelectValueRef.current = setSelectValue;
        refDisabledRef.current = refDisabled;
        setRefDisabledRef.current = setRefDisabled;
    });
    useEffect(() => { if (rest?.disabled !== undefined) setRefDisabled(!!rest.disabled); }, [rest?.disabled]);
    useEffect(() => {
        if (defaultValue == null || (defaultValue === '' && singleSelectOptions.length === 0)) return;
        const phVal = (placeholder != null && typeof placeholder === 'object') ? (placeholder.value ?? '') : '';
        const phLabel = (placeholder != null && typeof placeholder === 'object') ? (placeholder.text ?? placeholder.value ?? 'Select an option') : (String(placeholder ?? 'Select an option'));
        if (phVal !== '' && optionValueMatches(defaultValue, phVal)) {
            setSelectValue({ value: phVal, label: phLabel });
            return;
        }
        if (singleSelectOptions.length > 0) {
            const option = singleSelectOptions.find((opt) => opt != null && optionValueMatches(defaultValue, opt.value));
            if (option) setSelectValue(option);
        }
    }, [defaultValue, singleSelectOptions, placeholder]);
    useEffect(() => {
        if (!isSingleSelectReactSelect) return;
        if (!selectProxyRef.current) {
            const target = { value: '', disabled: false };
            selectProxyRef.current = new Proxy(target, {
                get(_, prop) {
                    if (prop === 'value') return (selectValueRef.current?.value !== undefined && selectValueRef.current?.value !== null) ? selectValueRef.current.value : '';
                    if (prop === 'disabled') return refDisabledRef.current;
                    return target[prop];
                },
                set(_, prop, value) {
                    if (prop === 'value') {
                        if (value === '' || value === undefined || value === null) setSelectValueRef.current(null);
                        else {
                            const opts = optionsRef.current;
                            if (opts?.length) {
                                const option = opts.find((opt) => opt != null && optionValueMatches(value, opt.value));
                                setSelectValueRef.current(option ?? null);
                            }
                        }
                    } else if (prop === 'disabled') setRefDisabledRef.current(!!value);
                    target[prop] = value;
                    return true;
                },
            });
        }
        inputRef.current = selectProxyRef.current;
    }, [isSingleSelectReactSelect]);
    useEffect(() => {
        if (!isSingleSelectReactSelect) return;
        const valueEmpty = valueProp === '' || valueProp === null || valueProp === undefined;
        const prevValue = previousValuePropRef.current;
        previousValuePropRef.current = valueProp;

        if (valueEmpty) {
            const hasPlaceholder = placeholder != null && placeholder !== '';
            const hasEmptyInOptions = singleSelectOptions.some((opt) => opt != null && (opt.value === '' || opt.value == null || opt.value === undefined || opt.value === 0));
            if (!hasPlaceholder && singleSelectOptions.length > 0 && !hasEmptyInOptions && name !== 'reportYearID') {
                const currentSelection = selectValueRef.current;
                const userAlreadySelected = currentSelection != null && currentSelection !== undefined;
                if (!userAlreadySelected) {
                    const firstOption = singleSelectOptions[0];
                    setSelectValue(firstOption);
                    const onChangeFromRest = rest?.onChange;
                    if (onChangeFromRest && firstOption != null) {
                        const value = firstOption.value ?? '';
                        onChangeFromRest({
                            target: { value, name, id: fieldName || name || undefined, type: 'select-one' },
                            preventDefault: () => { },
                            stopPropagation: () => { },
                        });
                    }
                }
                return;
            }
            const parentJustCleared = prevValue !== undefined && prevValue !== null && prevValue !== '' && valueEmpty;
            if (parentJustCleared) {
                const existingEmpty = singleSelectOptions.find((opt) => opt != null && (opt.value === '' || opt.value == null || opt.value === undefined || opt.value === 0));
                setSelectValue(existingEmpty ?? { value: '', label: 'Select an option' });
            }
            return;
        }
        const phVal = (placeholder != null && typeof placeholder === 'object') ? (placeholder.value ?? '') : '';
        const phLabel = (placeholder != null && typeof placeholder === 'object') ? (placeholder.text ?? placeholder.value ?? 'Select an option') : (String(placeholder ?? 'Select an option'));
        if (phVal !== '' && optionValueMatches(valueProp, phVal)) {
            setSelectValue({ value: phVal, label: phLabel });
            return;
        }
        if (singleSelectOptions.length === 0) return;
        const option = singleSelectOptions.find((opt) => opt != null && optionValueMatches(valueProp, opt.value));
        setSelectValue(option ?? null);
    }, [valueProp, singleSelectOptions, isSingleSelectReactSelect, placeholder, rest?.onChange, fieldName, name]);

    useEffect(() => {
        if (customOptions != null) {
            if (customOptions.indexOf(";") > -1) setCustomOptionsValues(customOptions.split(";"));
            else setCustomOptionsValues([customOptions]);
        }
        registerField({
            name: fieldName,
            ref: inputRef,
            getValue: ref => {
                if (inputType === 'listtreeselect') {
                    const v = treeValueRef.current;
                    if (v == null) return '';
                    return Array.isArray(v) ? JSON.stringify(v) : String(v);
                }
                if (!ref?.current) return null;
                if (inputType === "date") return ref.current.props?.selected ?? null;
                return ref.current.value ?? null;
            },
            setValue: (ref, value) => {
                if (!ref?.current) return;
                if (inputType === "date") { setStartDate(new Date(value)); ref.current.value = value; return; }
                ref.current.value = value;
            },
            clearValue: ref => { if (ref?.current) ref.current.value = ''; },
        });
    }, [fieldName, registerField, inputType]);

    useEffect(() => {
        if (inputType !== 'multiselectcustom') return;
        if (valueProp == null || valueProp === '') {
            setMultipleValues([]);
            return;
        }
        const next = Array.isArray(valueProp)
            ? valueProp
            : (typeof valueProp === 'string' && valueProp.startsWith('[') && valueProp.endsWith(']')
                ? (() => { try { return JSON.parse(valueProp); } catch { return []; } })()
                : []);
        setMultipleValues(Array.isArray(next) ? next : []);
    }, [inputType, valueProp]);

    useEffect(() => {
        if (inputType !== 'listtreeselect' || !listTreeID) {
            if (inputType === 'listtreeselect') {
                setTreeData([]);
                setTreeValue(undefined);
                treeValueRef.current = undefined;
                setTreeLazyExpand(true);
            }
            lastListTreeFetchKeyRef.current = '';
            return;
        }

        if (Array.isArray(parentItemIDsFilter)) {
            if (filterSignature === 'EMPTY') {
                const emptyKey = `filter|${listTreeID}|EMPTY|${optionsModeView}|${allowSelectOnlyChildrenNodes ? '1' : '0'}|${effectiveMaxTreeDepth ?? ''}`;
                if (lastListTreeFetchKeyRef.current === emptyKey) return;
                lastListTreeFetchKeyRef.current = emptyKey;
                setTreeLazyExpand(false);
                setTreeData([]);
                setTreeLoading(false);
                return;
            }
            const filterFetchKey = `filter|${listTreeID}|${filterSignature}|${optionsModeView}|${allowSelectOnlyChildrenNodes ? '1' : '0'}|${effectiveMaxTreeDepth ?? ''}`;
            if (lastListTreeFetchKeyRef.current === filterFetchKey) {
                return;
            }
            lastListTreeFetchKeyRef.current = filterFetchKey;

            setTreeData([]);
            setTreeValue(undefined);
            treeValueRef.current = undefined;
            setTreeLoading(true);
            setTreeLazyExpand(false);

            fetchListTreeFlatByListTreeID(listTreeID)
                .then(flat => {
                    const parentSet = new Set(filterSignature.split(',').map(Number));
                    const children = (flat || []).filter(
                        i => i.parentID != null && parentSet.has(Number(i.parentID)),
                    );
                    setTreeData(
                        children.map(item => ({
                            title: item.value,
                            value: item.listTreeItemID,
                            key: item.listTreeItemID,
                            depth: 0,
                            isLeaf: true,
                            disabled: shouldDisableTreeNode(
                                { hasChildren: false },
                                optionsModeView,
                                allowSelectOnlyChildrenNodes,
                            ),
                            children: [],
                        })),
                    );
                })
                .catch(() => {
                    lastListTreeFetchKeyRef.current = '';
                })
                .finally(() => setTreeLoading(false));
            return;
        }

        const useFullTree = hasPersistedListTreeValue(valueProp, optionsModeView);
        const fetchKey = `${listTreeID}|${useFullTree ? 'full' : 'lazy'}|${optionsModeView}|${allowSelectOnlyChildrenNodes ? '1' : '0'}|${effectiveMaxTreeDepth ?? ''}`;
        if (lastListTreeFetchKeyRef.current === fetchKey) {
            return;
        }
        lastListTreeFetchKeyRef.current = fetchKey;

        setTreeData([]);
        setTreeValue(undefined);
        treeValueRef.current = undefined;
        setTreeLoading(true);

        const onFetchError = () => {
            lastListTreeFetchKeyRef.current = '';
        };

        if (useFullTree) {
            setTreeLazyExpand(false);
            const depthParam = effectiveMaxTreeDepth != null ? `&maxDepth=${effectiveMaxTreeDepth}` : '';
            Api.get(`ListTree/getItems?listTreeID=${listTreeID}${depthParam}`)
                .then(result => {
                    const flat = result?.data?.response ?? [];
                    setTreeData(buildTreeDataFromFlatItems(flat, optionsModeView, allowSelectOnlyChildrenNodes, effectiveMaxTreeDepth));
                })
                .catch(onFetchError)
                .finally(() => setTreeLoading(false));
        } else {
            setTreeLazyExpand(true);
            Api.get(`ListTree/getChildrenItems?listTreeID=${listTreeID}`)
                .then(async result => {
                    const rootItems = result?.data?.response ?? [];
                    const childDepth = 1;
                    const reachedRootLimit =
                        effectiveMaxTreeDepth != null && effectiveMaxTreeDepth <= 0;
                    const reachedChildLimit =
                        effectiveMaxTreeDepth != null && childDepth >= effectiveMaxTreeDepth;

                    // Preload root + first level so opening the tree does not depend on first expand click.
                    const firstLevelByRoot = await Promise.all(
                        rootItems.map(item => {
                            if (reachedRootLimit || !item.hasChildren) {
                                return Promise.resolve({ rootID: item.listTreeItemID, children: [] });
                            }
                            return Api.get(
                                `ListTree/getChildrenItems?listTreeID=${listTreeID}&parentID=${item.listTreeItemID}`,
                            )
                                .then(childResult => ({
                                    rootID: item.listTreeItemID,
                                    children: childResult?.data?.response ?? [],
                                }))
                                .catch(() => ({ rootID: item.listTreeItemID, children: [] }));
                        }),
                    );
                    const firstLevelMap = new Map(
                        firstLevelByRoot.map(entry => [entry.rootID, entry.children]),
                    );

                    setTreeData(
                        rootItems.map(item => {
                            const firstLevelChildren = firstLevelMap.get(item.listTreeItemID) || [];
                            return {
                                title: item.value,
                                value: item.listTreeItemID,
                                key: item.listTreeItemID,
                                depth: 0,
                                isLeaf: !item.hasChildren || reachedRootLimit,
                                disabled: shouldDisableTreeNode(
                                    {
                                        hasChildren: item.hasChildren && !reachedRootLimit,
                                    },
                                    optionsModeView,
                                    allowSelectOnlyChildrenNodes,
                                ),
                                children: firstLevelChildren.map(child => ({
                                    title: child.value,
                                    value: child.listTreeItemID,
                                    key: child.listTreeItemID,
                                    depth: childDepth,
                                    isLeaf: !child.hasChildren || reachedChildLimit,
                                    disabled: shouldDisableTreeNode(
                                        {
                                            hasChildren: child.hasChildren && !reachedChildLimit,
                                        },
                                        optionsModeView,
                                        allowSelectOnlyChildrenNodes,
                                    ),
                                    children: [],
                                })),
                            };
                        }),
                    );
                })
                .catch(onFetchError)
                .finally(() => setTreeLoading(false));
        }
    }, [inputType, listTreeID, valueProp, optionsModeView, allowSelectOnlyChildrenNodes, effectiveMaxTreeDepth, parentItemIDsFilter, filterSignature]);

    useEffect(() => {
        if (inputType !== 'listtreeselect') return;
        let next;
        if (valueProp == null || valueProp === '') {
            next = undefined;
        } else if (optionsModeView === 2) {
            try {
                const arr = typeof valueProp === 'string' ? JSON.parse(valueProp) : valueProp;
                next = Array.isArray(arr) ? arr.map(Number) : [];
            } catch { next = []; }
        } else {
            const n = Number(valueProp);
            next = Number.isNaN(n) ? undefined : n;
        }
        setTreeValue(next);
        treeValueRef.current = next;
    }, [inputType, valueProp, optionsModeView]);

    const loadTreeData = useCallback((node) => {
        if (effectiveMaxTreeDepth != null && (node.depth ?? 0) >= effectiveMaxTreeDepth) {
            return Promise.resolve();
        }
        return Api.get(`ListTree/getChildrenItems?listTreeID=${listTreeID}&parentID=${node.value}`)
            .then(result => {
                const items = result?.data?.response ?? [];
                const childDepth = (node.depth ?? 0) + 1;
                const reachedLimit = effectiveMaxTreeDepth != null && childDepth >= effectiveMaxTreeDepth;
                const children = items.map(item => ({
                    title: item.value,
                    value: item.listTreeItemID,
                    key: item.listTreeItemID,
                    depth: childDepth,
                    isLeaf: !item.hasChildren || reachedLimit,
                    disabled: shouldDisableTreeNode(
                        { hasChildren: item.hasChildren && !reachedLimit },
                        optionsModeView,
                        allowSelectOnlyChildrenNodes,
                    ),
                    children: [],
                }));
                setTreeData(prev => updateTreeData(prev, node.key, children));
            })
            .catch(() => {});
    }, [listTreeID, optionsModeView, allowSelectOnlyChildrenNodes, effectiveMaxTreeDepth]);

    const handleDateSelect = () => {
        return startDate;
    }

    const handleDate = (date) => {
        setStartDate(date)
    }

    const handleInputFocus = () => {
        setIsFocused(true);
    }

    const handleInputBlur = () => {
        setIsFocused(false);

        setIsFilled(!!inputRef.current?.value);
    }

    // Considera "vazio" para evitar duplicata: '', null, undefined ou 0 (usado em vários forms como placeholder).
    const isEmptyOption = (opt) => opt != null && (opt.value === '' || opt.value == null || opt.value === undefined || opt.value === 0);

    // Com placeholder, a opção vazia deve sempre aparecer na lista (uma única vez) para permitir voltar a ela.
    let selectOptions;
    let selectValueFallback;
    if (hasPlaceholder) {
        const existingEmptyOption = singleSelectOptions.find(isEmptyOption);
        const emptyOption = existingEmptyOption ?? { value: placeholderOptionValue, label: selectPlaceholderText };
        const restOptions = existingEmptyOption ? singleSelectOptions.filter((opt) => opt !== existingEmptyOption) : singleSelectOptions;
        selectOptions = [emptyOption, ...restOptions];
        selectValueFallback = emptyOption;
    } else {
        if (name === 'reportYearID') {
            const emptyOption = { value: '', label: 'Select a Report Year' };
            const existingInOptions = singleSelectOptions.find(isEmptyOption);
            const rest = existingInOptions ? singleSelectOptions.filter((opt) => opt !== existingInOptions) : singleSelectOptions;
            selectOptions = [emptyOption, ...rest];
            selectValueFallback = emptyOption;
        } else {
            selectOptions = singleSelectOptions;
            selectValueFallback = singleSelectOptions.length > 0 ? singleSelectOptions[0] : null;
        }
    }

    return (
        <Container isErrored={!!error} isFilled={isFilled} isFocused={isFocused} style={{ padding: styleGlide }}>
            {isSingleSelectReactSelect && (
                <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                    <Select
                        inputId={reactSelectInputId}
                        name={name}
                        title={name}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        options={selectOptions}
                        value={selectValue ?? selectValueFallback}
                        onChange={(newValue) => {
                            setSelectValue(newValue);
                            setIsFilled(!!newValue);
                            if (onChangeProp) {
                                const value = newValue?.value ?? '';
                                onChangeProp({
                                    target: { value, name, id: reactSelectInputId || name || undefined, type: 'select-one' },
                                    preventDefault: () => { },
                                    stopPropagation: () => { },
                                });
                            }
                        }}
                        placeholder={selectPlaceholderText}
                        isSearchable
                        isClearable={false}
                        isDisabled={refDisabled}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            ...formSelectStyles,
                            control: (base, state) => ({
                                ...formSelectControlFullWidth(base, state),
                                ...(error ? { borderColor: '#ff0000' } : {}),
                                ...(blockedValueAlert && state.isDisabled
                                    ? { border: '1px solid #e71700', borderColor: '#e71700' }
                                    : {}),
                            }),
                        }}
                        noOptionsMessage={() => 'no data'}
                        classNamePrefix={name}
                        {...restSelect}
                        className={reactSelectOuterClassName(classNameProp, blockedValueAlert)}
                    />
                </div>
            )}

            {inputType === "selectcustom" && (refNumber === "7309" || refNumber === "7316") && (
                <Select
                    inputId={reactSelectInputId}
                    name={name}
                    title={name}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    ref={inputRef}
                    options={customOptionsValues.map((option) => ({ value: option, label: option }))}
                    value={multipleValues}
                    isMulti
                    isSearchable
                    closeMenuOnSelect={false}
                    onChange={(a) => setMultipleValues(a)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                        ...formSelectStyles,
                        control: (base, state) => ({
                            ...formSelectStyles.control(base, state),
                            ...(blockedValueAlert && state.isDisabled
                                ? { border: '1px solid #e71700', borderColor: '#e71700' }
                                : {}),
                        }),
                    }}
                    classNamePrefix={name}
                    {...restSelect}
                    className={['col-md-12', reactSelectOuterClassName(classNameProp, blockedValueAlert)].filter(Boolean).join(' ')}
                    isDisabled={refDisabled}
                />
            )}

            {inputType === "multiselectcustom" && (
                <Select
                    inputId={reactSelectInputId}
                    name={name}
                    title={name}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    ref={inputRef}
                    options={customOptionsValues.map((option) => ({ value: option, label: option }))}
                    value={multipleValues}
                    isMulti
                    isSearchable
                    closeMenuOnSelect={false}
                    onChange={(newValue) => {
                        setMultipleValues(newValue || []);
                        if (onChangeProp) {
                            onChangeProp(newValue);
                        }
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                        ...formSelectStyles,
                        control: (base, state) => ({
                            ...formSelectStyles.control(base, state),
                            minWidth: '200px',
                            ...(blockedValueAlert && state.isDisabled
                                ? { border: '1px solid #e71700', borderColor: '#e71700' }
                                : {}),
                        }),
                    }}
                    classNamePrefix={name}
                    {...restSelect}
                    className={['col-md-12', reactSelectOuterClassName(classNameProp, blockedValueAlert)].filter(Boolean).join(' ')}
                    isDisabled={refDisabled}
                />
            )}

            {inputType === "check" &&
                <FormCheck onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    defaultChecked={valueProp !== undefined && valueProp !== null ? !!valueProp : checked}
                    defaultValue={valueProp != null ? undefined : checked}
                    checked={valueProp !== undefined && valueProp !== null ? !!valueProp : checked}
                    ref={inputRef}
                    {...restWithClassName} />
            }


            {inputType === "textarea" &&
                <textarea onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    value={valueProp !== undefined && valueProp !== null ? String(valueProp) : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    ref={inputRef}
                    {...restWithClassName} />
            }

            {inputType === "text" &&
                <input onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    value={valueProp !== undefined && valueProp !== null ? String(valueProp) : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    ref={inputRef}
                    type="text"
                    {...restWithClassName} />
            }

            {(inputType === "number") &&
                <input onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    value={valueProp !== undefined && valueProp !== null ? valueProp : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    ref={inputRef}
                    step="0"
                    presicion={0}
                    type={inputType}
                    min={0}
                    {...restWithClassName} />
            }

            {(inputType === "decimal") &&
                <input onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    value={valueProp !== undefined && valueProp !== null ? valueProp : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    step="0.001"
                    presicion={3}   //very important
                    ref={inputRef}
                    type={"number"}
                    min={0}
                    {...restWithClassName} />
            }

            {(inputType === "date") &&
                <DatePicker ref={inputRef} onSelect={handleDateSelect} selected={startDate} onChange={(date) => { setStartDate(date) }} dateFormat="dd-MMM-yyyy" />
            }

            {((inputType === "input" || inputType === "" || inputType == null) && !isSimulation) &&
                <textarea onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    value={valueProp !== undefined && valueProp !== null ? String(valueProp) : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    ref={inputRef}
                    {...restWithClassName} />

            }

            {((inputType === "input" || inputType === "" || inputType == null) && isSimulation) &&
                <input onFocus={handleInputFocus}
                    autoComplete={"off"}
                    onBlur={handleInputBlur}
                    type={inputType === "input" ? "text" : inputType}
                    value={valueProp !== undefined && valueProp !== null ? String(valueProp) : undefined}
                    defaultValue={valueProp != null ? undefined : defaultValue}
                    placeholder={placeholder}
                    ref={inputRef}
                    {...restWithClassName} />
            }
            {(inputType === "password") &&
                <>
                    <div className={"d-block"}>
                        <input onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            type={inputType}
                            value={valueProp !== undefined && valueProp !== null ? String(valueProp) : undefined}
                            defaultValue={valueProp != null ? undefined : defaultValue}
                            placeholder={placeholder}
                            ref={inputRef}
                            onChange={(e) => {
                                setInputValue(
                                    e.target.value
                                );
                            }}
                        />
                        <PasswordStrengthBar
                            password={inputValue}
                            minLength={10}
                        />
                    </div>
                </>
            }

            {(inputType === "colorpicker") &&
                <div style={{ width: "100%" }}>
                    <input
                        type="color"
                        value={defaultValue}
                        ref={inputRef}
                        placeholder={placeholder}
                        style={{ width: '60px', height: '30px', cursor: 'pointer' }}
                        {...restWithClassName}
                    />
                </div>
            }

            {inputType === 'listtreeselect' && (
                <TreeSelect
                    treeData={treeData}
                    value={treeValue}
                    loadData={treeLazyExpand && treeData.length > 0 ? loadTreeData : undefined}
                    multiple={optionsModeView === 2}
                    treeCheckable={optionsModeView === 2}
                    treeCheckStrictly={false}
                    showCheckedStrategy={TreeSelect.SHOW_CHILD}
                    showSearch
                    allowClear
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                    loading={treeLoading}
                    disabled={!!restSelect.disabled}
                    placeholder="Select..."
                    getPopupContainer={() =>
                        typeof document !== 'undefined' ? document.body : undefined
                    }
                    placement="bottomLeft"
                    onChange={(val) => {
                        if (optionsModeView === 2) {
                            const rawIds = Array.isArray(val)
                                ? val.map(v => (v != null && typeof v === 'object' ? (v.value ?? v) : v))
                                : [];

                            setTreeValue(rawIds);
                            treeValueRef.current = rawIds;

                            // Verifica se algum ID retornado pelo AntDesign é um nó pai (isLeaf: false).
                            // Isso acontece com lazy loading quando o nó pai não foi expandido antes de ser marcado
                            // e o AntDesign (SHOW_CHILD) não tem os filhos carregados em treeData.
                            const hasNonLeaf = rawIds.some(id => {
                                const node = findNodeByValueInTree(treeData, id);
                                return node ? !node.isLeaf : false;
                            });

                            if (!hasNonLeaf) {
                                if (onChangeProp) onChangeProp(JSON.stringify(rawIds));
                            } else {
                                // Busca a árvore completa (flat) e expande os IDs não-folha
                                // até seus descendentes folha reais.
                                fetchListTreeFlatByListTreeID(listTreeID)
                                    .then(flat => {
                                        const finalIds = resolveToLeafIds(rawIds, flat);
                                        treeValueRef.current = finalIds;
                                        if (onChangeProp) onChangeProp(JSON.stringify(finalIds));
                                    })
                                    .catch(() => {
                                        if (onChangeProp) onChangeProp(JSON.stringify(rawIds));
                                    });
                            }
                        } else {
                            const strValue = val != null ? String(val) : '';
                            const next = val ?? undefined;
                            setTreeValue(next);
                            treeValueRef.current = next;
                            if (onChangeProp) onChangeProp(strValue);
                        }
                    }}
                />
            )}

            {isDash != null &&
                <Error title={error} className={hideAlert ? "hide" : ""}>
                    <FiAlertCircle color="#e71700" size={20} />
                </Error>
            }
        </Container>
    );
}

export default FormInput;
